import { IconButton, Input, InputGroup, InputRightElement, useMergeRefs, VisuallyHidden } from "@chakra-ui/react";
import { enGB } from "date-fns/locale/en-GB";
import { nb } from "date-fns/locale/nb";
import { forwardRef, useCallback, useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiX } from "react-icons/fi";
import { registerLocale, setDefaultLocale } from "react-datepicker";

registerLocale("nb", nb);
registerLocale("en-GB", enGB);
setDefaultLocale("nb");

export const PORTAL_ID = "datepicker-portal";

export const datepickerLocaleMap: Record<string, string> = {
  no: "nb-NO",
  en: "en-US",
};

export const usePickerLanguage = (withTime = false) => {
  const { t, i18n } = useTranslation();
  const dialogLabel = withTime ? t("Choose date and time") : t("Choose date");
  const calendarContainer = useCallback(
    ({ className, children, inline }: { className?: string; children?: ReactNode; inline?: boolean }) => (
      <div
        className={className}
        aria-label={dialogLabel}
        role={inline ? undefined : "dialog"}
        aria-modal={inline ? undefined : "true"}
        translate="no"
      >
        {children}
      </div>
    ),
    [dialogLabel],
  );
  return {
    locale: i18n.language === "en" ? "en-GB" : "nb",
    chooseDayAriaLabelPrefix: t("Choose"),
    disabledDayAriaLabelPrefix: t("Not available"),
    monthAriaLabelPrefix: t("Month"),
    calendarContainer,
  };
};

type PickerCustomInputProps = React.ComponentProps<typeof Input> & {
  onClear?: () => void;
  clearLabel?: string;
  isTypeable?: boolean;
};

export const PickerCustomInput = forwardRef<HTMLInputElement, PickerCustomInputProps>(
  ({ onClear, clearLabel, isTypeable = false, ...props }, ref) => {
    const offersClear = onClear !== undefined && !props.disabled;
    const clearable = offersClear && Boolean(props.value);
    const inputRef = useRef<HTMLInputElement>(null);
    const mergedRef = useMergeRefs(ref, inputRef);
    const input = (
      <Input
        ref={mergedRef}
        {...props}
        fontSize="md"
        maxWidth={{ sm: "full", md: "60" }}
        border="1px solid"
        borderColor="smInputBorder"
        backgroundColor="smBackground"
        rounded="md"
        p="4"
        pr={clearable ? "10" : "4"}
        readOnly={!isTypeable}
        cursor={isTypeable ? "text" : "pointer"}
      />
    );
    if (!offersClear) return input;
    return (
      <InputGroup maxWidth={{ sm: "full", md: "60" }}>
        {input}
        {clearable && (
          <InputRightElement h="full">
            <IconButton
              aria-label={clearLabel ?? ""}
              icon={<FiX />}
              size="xs"
              variant="ghost"
              onClick={(event) => {
                event.stopPropagation();
                onClear();
                inputRef.current?.focus();
              }}
            />
          </InputRightElement>
        )}
      </InputGroup>
    );
  },
);
PickerCustomInput.displayName = "PickerCustomInput";

export const parsePickerIsoDate = (
  iso: string | null | undefined,
  options: { warnOnInvalid?: boolean } = {},
): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (!isNaN(d.getTime())) return d;
  if (options.warnOnInvalid) {
    // eslint-disable-next-line no-console
    console.warn("Datepicker received an unparseable ISO string", { iso });
  }
  return null;
};

export const useDatepickerPortal = (): void => {
  useEffect(() => {
    if (!document.getElementById(PORTAL_ID)) {
      const el = document.createElement("div");
      el.id = PORTAL_ID;
      document.body.appendChild(el);
    }
  }, []);
};

export const SelectedDateAnnouncement = ({
  selected,
  withTime,
  locale,
}: {
  selected: Date | null;
  withTime: boolean;
  locale: string;
}) => {
  const { t } = useTranslation();
  const formatted = selected
    ? new Intl.DateTimeFormat(locale, withTime ? { dateStyle: "full", timeStyle: "short" } : { dateStyle: "full" }).format(
        selected,
      )
    : null;
  return (
    <VisuallyHidden role="status" aria-live="polite">
      {formatted ? t("Selected date: {{date}}", { date: formatted }) : ""}
    </VisuallyHidden>
  );
};
